"use strict";
/**
 * Pure, dependency-free matcher that auto-sorts provided files into a channel's placements by aspect
 * ratio. Shared by the web manual-creation popover (preview + name prefill) and the api route, so the
 * client preview and the server's expectations agree. No Prisma / DOM imports.
 */
Object.defineProperty(exports, "__esModule", { value: true });
exports.PLACEMENT_MATCH_TOLERANCE = void 0;
exports.isFileMediaCompatibleWithPlacement = isFileMediaCompatibleWithPlacement;
exports.parsePlacementAspectRatio = parsePlacementAspectRatio;
exports.isFileEligibleForPlacement = isFileEligibleForPlacement;
exports.matchFilesToPlacements = matchFilesToPlacements;
/**
 * Relative aspect-ratio tolerance for auto-sort eligibility (6%). Wide enough to absorb export/pixel
 * noise (a 9:16 file measured as 0.5626 vs 0.5625), tight enough to keep every distinct bucket apart —
 * the closest two in use, 16:9 (1.778) and 1.91:1 (1.91), are ~7.4% apart.
 */
exports.PLACEMENT_MATCH_TOLERANCE = 0.06;
/** Whether a placement accepting `mediaSupport` can hold a file of the given category. */
function isFileMediaCompatibleWithPlacement(mediaSupport, category) {
    if (category === 'IMAGE')
        return mediaSupport === 'STATIC_ONLY' || mediaSupport === 'BOTH';
    if (category === 'VIDEO')
        return mediaSupport === 'VIDEO_ONLY' || mediaSupport === 'BOTH';
    return false;
}
/** Parse a `"w:h"` placement aspect ratio into a numeric width/height, or null when malformed. */
function parsePlacementAspectRatio(aspectRatio) {
    const parts = aspectRatio.split(':');
    if (parts.length !== 2)
        return null;
    const width = Number(parts[0]);
    const height = Number(parts[1]);
    if (!width || !height || Number.isNaN(width) || Number.isNaN(height))
        return null;
    return width / height;
}
/** Whether a file is eligible for a placement: media-compatible AND within the ratio tolerance. */
function isFileEligibleForPlacement(file, placement) {
    if (file.aspectRatio === null)
        return false;
    const placementRatio = parsePlacementAspectRatio(placement.aspectRatio);
    if (placementRatio === null || placementRatio <= 0)
        return false;
    if (!isFileMediaCompatibleWithPlacement(placement.mediaSupport, file.category))
        return false;
    return Math.abs(file.aspectRatio - placementRatio) / placementRatio <= exports.PLACEMENT_MATCH_TOLERANCE;
}
/**
 * Auto-sort `files` into `placements`. A file fills every eligible placement of its aspect ratio
 * (fan-out), but distinct files are spread across distinct same-ratio placements first. Returns the
 * default assignment the user then sees and may override; files that match nothing are `unplaced`.
 */
function matchFilesToPlacements(files, placements) {
    const placedByPlacementSlug = {};
    const usedFileUuids = new Set();
    // File uuids eligible for a placement, closest aspect ratio first, then provided order.
    const candidatesFor = (placement) => {
        const placementRatio = parsePlacementAspectRatio(placement.aspectRatio);
        if (placementRatio === null)
            return [];
        return files
            .map((file, index) => ({ file, index }))
            .filter(({ file }) => isFileEligibleForPlacement(file, placement))
            .sort((a, b) => {
            const distanceA = Math.abs(a.file.aspectRatio - placementRatio);
            const distanceB = Math.abs(b.file.aspectRatio - placementRatio);
            return distanceA !== distanceB ? distanceA - distanceB : a.index - b.index;
        })
            .map(({ file }) => file.fileUuid);
    };
    // Pass 1 — give each placement its closest still-unused file, so distinct files spread out.
    for (const placement of placements) {
        const fresh = candidatesFor(placement).find((uuid) => !usedFileUuids.has(uuid));
        if (fresh) {
            placedByPlacementSlug[placement.slug] = fresh;
            usedFileUuids.add(fresh);
        }
    }
    // Pass 2 — fan a file out into any still-empty placement it matches.
    for (const placement of placements) {
        if (placedByPlacementSlug[placement.slug])
            continue;
        const best = candidatesFor(placement)[0];
        if (best) {
            placedByPlacementSlug[placement.slug] = best;
            usedFileUuids.add(best);
        }
    }
    const unplacedFileUuids = files.filter((file) => !usedFileUuids.has(file.fileUuid)).map((file) => file.fileUuid);
    return { placedByPlacementSlug, unplacedFileUuids };
}
